import React, { useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { AlertCircle, ChevronLeft } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import api from "@/lib/api"
import {
  PatientResponse,
  PatientAPCMConsentResponse,
  CarePlanResponse,
  HealthSummaryResponse,
  ActionItemResponse,
  PatientCommunicationHistoryResponse,
} from "@/lib/types"
import { usePatientsStore } from "@/stores/patientsStore"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import { useConsentsStore } from "@/stores/consentsStore"

import PatientHeader from "@/components/patients/v2/PatientHeader"
import PatientOverview from "@/components/patients/v2/PatientOverview"
import PatientCampaigns from "@/components/patients/v2/PatientCampaigns"
import PatientCalls from "@/components/patients/v2/PatientCalls"
import PatientCarePlanTab from "@/components/patients/v2/PatientCarePlanTab"
import PatientMonthlyPlansTab from "@/components/patients/v2/PatientMonthlyPlansTab"
import PatientActionItemsTab from "@/components/patients/v2/PatientActionItemsTab"
import PatientConsentModal from "@/components/PatientConsentModal"
import CarePlanModal from "@/components/CarePlanModal"

type TabValue = "overview" | "campaigns" | "calls" | "care-plan" | "monthly-plans" | "action-items"

export default function PatientDetailPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { id: patientId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  // State
  const [patient, setPatient] = useState<PatientResponse | null>(null)
  const [patientConsent, setPatientConsent] = useState<PatientAPCMConsentResponse | null>(null)
  const [carePlans, setCarePlans] = useState<CarePlanResponse[]>([])
  const [healthSummaries, setHealthSummaries] = useState<HealthSummaryResponse[]>([])
  const [actionItems, setActionItems] = useState<ActionItemResponse[]>([])
  const [communicationHistory, setCommunicationHistory] = useState<PatientCommunicationHistoryResponse | null>(null)
  const [patientCampaigns, setPatientCampaigns] = useState<{
    id: string
    definitionId: string
    name: string
    status: "active" | "paused" | "completed" | "removed"
    nextCallDate?: string | null
    attempts: number
    maxAttempts: number
    enrolledAt: string
    pauseStart?: string | null
    pauseEnd?: string | null
    pauseReason?: string | null
  }[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modals
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false)
  const [isCarePlanModalOpen, setIsCarePlanModalOpen] = useState(false)
  const [selectedCarePlan, setSelectedCarePlan] = useState<CarePlanResponse | null>(null)

  // Stores
  const { getPatient, updatePatient } = usePatientsStore()
  const { selectedOrganizationId, organizations, setSelectedOrganizationId } = useOrganizationsStore()
  const { fetchPatientConsents, getLatestConsentForPatient } = useConsentsStore()

  // Active tab from URL
  const activeTab = (searchParams.get("tab") as TabValue) || "overview"

  const setActiveTab = (tab: TabValue) => {
    setSearchParams({ tab }, { replace: true })
  }

  // Auto-switch organization if patient belongs to a different org
  useEffect(() => {
    if (!patientId || organizations.length === 0) return

    const checkAndSwitchOrg = async () => {
      try {
        const patientData = await api.getPatient(patientId)

        // Get all org IDs the patient belongs to
        const patientOrgIds: string[] = []
        if (patientData.organization_id) {
          patientOrgIds.push(patientData.organization_id)
        }
        if (patientData.organizations && Array.isArray(patientData.organizations)) {
          patientData.organizations.forEach((org: any) => {
            const orgId = typeof org === 'string' ? org : org?.id || org?.organization_id
            if (orgId && !patientOrgIds.includes(orgId)) {
              patientOrgIds.push(orgId)
            }
          })
        }

        // Check if current org is valid for this patient
        const currentOrgIsValid = selectedOrganizationId && patientOrgIds.includes(selectedOrganizationId)

        if (!currentOrgIsValid && patientOrgIds.length > 0) {
          // Find an org the user has access to
          const accessibleOrg = organizations.find(org => patientOrgIds.includes(org.id))
          if (accessibleOrg) {
            toast({
              title: "Organization switched",
              description: `Switched to ${accessibleOrg.name} to view this patient.`,
            })
            setSelectedOrganizationId(accessibleOrg.id)
          }
        }
      } catch (err) {
        console.error("Error checking patient organization:", err)
      }
    }

    checkAndSwitchOrg()
  }, [patientId, organizations])

  // Fetch patient data
  useEffect(() => {
    if (!patientId || !selectedOrganizationId) return

    const fetchData = async () => {
      setIsLoading(true)
      setError(null)

      // Try to use cached patient first
      const storedPatient = getPatient(patientId)
      if (storedPatient) {
        setPatient(storedPatient)
      }

      try {
        // Fetch all data in parallel
        const [
          patientData,
          consents,
          carePlansData,
          commHistoryData,
          actionItemsData,
          campaignsData,
        ] = await Promise.all([
          api.getPatient(patientId),
          fetchPatientConsents(selectedOrganizationId, patientId),
          api.getCarePlansAndHealthSummaries(patientId, selectedOrganizationId).catch(() => ({ care_plans: [], health_summaries: [] })),
          api.getPatientCommunicationHistory(patientId, { limit: 100 }).catch(() => null),
          api.getOrganizationActionItems(selectedOrganizationId).catch(() => ({ items: [] })),
          api.getPatientCampaigns(patientId).catch(() => []),
        ])

        setPatient(patientData)
        updatePatient(patientId, patientData)
        setPatientConsent(consents.length > 0 ? consents[0] : null)
        setCarePlans(carePlansData.care_plans || [])
        setHealthSummaries(carePlansData.health_summaries || [])
        setCommunicationHistory(commHistoryData)
        // Filter action items by patient_id
        const patientActionItems = (actionItemsData.items || []).filter(
          (item: ActionItemResponse) => item.patient_id === patientId
        )
        setActionItems(patientActionItems)

        // Transform campaigns data to match expected interface
        const transformedCampaigns = (campaignsData || []).map((c) => ({
          id: c.id,
          definitionId: c.definition_id,
          name: c.name,
          status: c.status?.toLowerCase() as "active" | "paused" | "completed" | "removed",
          nextCallDate: c.next_call_date,
          attempts: c.attempts || 0,
          maxAttempts: c.max_attempts || 0,
          enrolledAt: c.enrolled_at || c.created_at,
          pauseStart: c.pause_start,
          pauseEnd: c.pause_end,
          pauseReason: c.pause_reason,
        }))
        setPatientCampaigns(transformedCampaigns)
      } catch (err: any) {
        console.error("Error fetching patient data:", err)
        if (!storedPatient) {
          setError(err?.response?.data?.detail || "Failed to load patient data")
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [patientId, selectedOrganizationId])

  // Handle actions
  const handleEditPatient = () => {
    navigate(`/patients/${patientId}/edit`)
  }

  const handleUpdateConsent = () => {
    setIsConsentModalOpen(true)
  }

  const handleEditCarePlan = (carePlan?: CarePlanResponse) => {
    setSelectedCarePlan(carePlan || null)
    setIsCarePlanModalOpen(true)
  }

  const handleApproveCarePlan = async (carePlanId: string) => {
    try {
      await api.updateCarePlanStatus(carePlanId, "approved")
      toast({ title: "Care plan approved" })
      // Refresh care plans
      if (patientId && selectedOrganizationId) {
        const data = await api.getCarePlansAndHealthSummaries(patientId, selectedOrganizationId)
        setCarePlans(data.care_plans || [])
      }
    } catch (err) {
      toast({ title: "Error approving care plan", variant: "destructive" })
    }
  }

  const handleUnapproveCarePlan = async (carePlanId: string) => {
    try {
      await api.updateCarePlanStatus(carePlanId, "draft")
      toast({ title: "Care plan unapproved" })
      // Refresh care plans
      if (patientId && selectedOrganizationId) {
        const data = await api.getCarePlansAndHealthSummaries(patientId, selectedOrganizationId)
        setCarePlans(data.care_plans || [])
      }
    } catch (err) {
      toast({ title: "Error unapproving care plan", variant: "destructive" })
    }
  }

  const handleCompleteActionItem = async (itemId: string) => {
    try {
      await api.updateActionItemStatus(itemId, { status: "completed" })
      toast({ title: "Action item completed" })
      // Refresh action items
      if (patientId && selectedOrganizationId) {
        const data = await api.getOrganizationActionItems(selectedOrganizationId)
        const patientActionItems = (data.items || []).filter(
          (item: ActionItemResponse) => item.patient_id === patientId
        )
        setActionItems(patientActionItems)
      }
    } catch (err) {
      toast({ title: "Error completing action item", variant: "destructive" })
    }
  }

  const handleReopenActionItem = async (itemId: string) => {
    try {
      await api.updateActionItemStatus(itemId, { status: "pending" })
      toast({ title: "Action item reopened" })
      if (patientId && selectedOrganizationId) {
        const data = await api.getOrganizationActionItems(selectedOrganizationId)
        const patientActionItems = (data.items || []).filter(
          (item: ActionItemResponse) => item.patient_id === patientId
        )
        setActionItems(patientActionItems)
      }
    } catch (err) {
      toast({ title: "Error reopening action item", variant: "destructive" })
    }
  }

  // Campaign action handlers
  const refreshCampaigns = async () => {
    if (!patientId) return
    try {
      const campaignsData = await api.getPatientCampaigns(patientId)
      const transformedCampaigns = (campaignsData || []).map((c) => ({
        id: c.id,
        definitionId: c.definition_id,
        name: c.name,
        status: c.status?.toLowerCase() as "active" | "paused" | "completed" | "removed",
        nextCallDate: c.next_call_date,
        attempts: c.attempts || 0,
        maxAttempts: c.max_attempts || 0,
        enrolledAt: c.enrolled_at || c.created_at,
        pauseStart: c.pause_start,
        pauseEnd: c.pause_end,
        pauseReason: c.pause_reason,
      }))
      setPatientCampaigns(transformedCampaigns)
    } catch (err) {
      console.error("Error refreshing campaigns:", err)
    }
  }

  const handlePauseCampaign = async (definitionId: string, enrollmentId: string) => {
    try {
      await api.pauseEnrollment(definitionId, enrollmentId)
      toast({ title: "Campaign paused" })
      await refreshCampaigns()
    } catch (err) {
      toast({ title: "Error pausing campaign", variant: "destructive" })
    }
  }

  const handleResumeCampaign = async (definitionId: string, enrollmentId: string) => {
    try {
      await api.resumeEnrollment(definitionId, enrollmentId)
      toast({ title: "Campaign resumed" })
      await refreshCampaigns()
    } catch (err) {
      toast({ title: "Error resuming campaign", variant: "destructive" })
    }
  }

  const handleRemoveFromCampaign = async (definitionId: string, enrollmentId: string) => {
    try {
      await api.removeEnrollment(definitionId, enrollmentId)
      toast({ title: "Removed from campaign" })
      await refreshCampaigns()
    } catch (err) {
      toast({ title: "Error removing from campaign", variant: "destructive" })
    }
  }

  const handlePostponeEnrollment = async (
    definitionId: string,
    enrollmentId: string,
    pauseStart: string,
    pauseEnd: string,
    reason?: string
  ) => {
    try {
      await api.postponeEnrollment(definitionId, enrollmentId, {
        pause_start: pauseStart,
        pause_end: pauseEnd,
        reason,
      })
      toast({ title: "Enrollment paused" })
      await refreshCampaigns()
    } catch (err) {
      toast({ title: "Error pausing enrollment", variant: "destructive" })
      throw err
    }
  }

  const handleClearEnrollmentPostpone = async (definitionId: string, enrollmentId: string) => {
    try {
      await api.clearEnrollmentPostpone(definitionId, enrollmentId)
      toast({ title: "Pause dates cleared" })
      await refreshCampaigns()
    } catch (err) {
      toast({ title: "Error clearing pause dates", variant: "destructive" })
      throw err
    }
  }

  // Transform data for components
  const recentActivity = (communicationHistory?.timeline || []).slice(0, 5).map((item) => ({
    id: item.id,
    type: item.type,
    title: item.type === "call"
      ? (item.strategy_name || "Call")
      : (item.strategy_name || "SMS Conversation"),
    description: item.type === "sms" ? (item.last_message ?? undefined) : undefined,
    date: item.timestamp,
    callId: item.type === "call" ? item.id : undefined,
  }))

  // Loading state
  if (isLoading && !patient) {
    return (
      <div className="min-h-screen">
        <div className="sticky top-0 z-10 bg-background border-b px-4 py-4 md:px-6">
          <Skeleton className="h-6 w-24 mb-2" />
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-full max-w-96" />
        </div>
        <div className="p-6 space-y-4">
          <Skeleton className="h-10 w-full max-w-lg" />
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate("/patients")}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Patients
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  // Not found state
  if (!patient) {
    return (
      <div className="p-6">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate("/patients")}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Patients
        </Button>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Not Found</AlertTitle>
          <AlertDescription>Patient not found</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Sticky Header */}
      <PatientHeader
        patient={patient}
        consentStatus={patientConsent?.consent_status}
        consentDate={patientConsent?.created_at}
        onEditPatient={handleEditPatient}
        onUpdateConsent={handleUpdateConsent}
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
        {/* Tab Navigation */}
        <div className="sticky top-0 md:top-[49px] z-[9] bg-card/95 backdrop-blur-sm border-b shadow-[0_1px_3px_0_rgba(0,0,0,0.04)]">
          <div className="px-4 md:px-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList className="h-10 w-max bg-transparent border-0 p-0 gap-0">
              <TabsTrigger
                value="overview"
                className="h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 md:px-4 text-sm whitespace-nowrap"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger
                value="campaigns"
                className="h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 md:px-4 text-sm whitespace-nowrap"
              >
                Campaigns
              </TabsTrigger>
              <TabsTrigger
                value="calls"
                className="h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 md:px-4 text-sm whitespace-nowrap"
              >
                Calls & SMS
              </TabsTrigger>
              <TabsTrigger
                value="monthly-plans"
                className="h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 md:px-4 text-sm whitespace-nowrap"
              >
                Monthly Plans
              </TabsTrigger>
              <TabsTrigger
                value="care-plan"
                className="h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 md:px-4 text-sm whitespace-nowrap"
              >
                Care Plans & Care Gaps
              </TabsTrigger>
              <TabsTrigger
                value="action-items"
                className="h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 md:px-4 text-sm whitespace-nowrap"
              >
                Action Items
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4 py-4 md:px-8 md:py-6">
          <TabsContent value="overview" className="mt-0">
            <PatientOverview
              patient={patient}
              consent={patientConsent}
              actionItems={actionItems.filter(i => i.status !== "completed").slice(0, 3).map(i => ({
                id: i.id,
                title: i.title,
                assignee: i.assigned_users?.[0]?.name || i.assignee || undefined,
                dueDate: i.due_date || undefined,
              }))}
              recentActivity={recentActivity}
              loading={isLoading}
              onUpdateConsent={handleUpdateConsent}
              onEditContact={handleEditPatient}
            />
          </TabsContent>

          <TabsContent value="campaigns" className="mt-0">
            <PatientCampaigns
              patientId={patientId!}
              campaigns={patientCampaigns}
              loading={isLoading}
              onPauseCampaign={handlePauseCampaign}
              onResumeCampaign={handleResumeCampaign}
              onRemoveFromCampaign={handleRemoveFromCampaign}
              onPostponeEnrollment={handlePostponeEnrollment}
              onClearEnrollmentPostpone={handleClearEnrollmentPostpone}
            />
          </TabsContent>

          <TabsContent value="calls" className="mt-0">
            <PatientCalls
              patientId={patientId!}
              communicationHistory={communicationHistory}
              loading={isLoading}
            />
          </TabsContent>

          <TabsContent value="care-plan" className="mt-0">
            <PatientCarePlanTab
              patientId={patientId!}
              carePlan={carePlans[0] || null}
              loading={isLoading}
              onEdit={() => handleEditCarePlan(carePlans[0])}
              onApprove={() => carePlans[0] && handleApproveCarePlan(carePlans[0].id)}
              onReject={() => carePlans[0] && handleUnapproveCarePlan(carePlans[0].id)}
              canApprove={true}
              organizationId={selectedOrganizationId || undefined}
              onSaveSuccess={() => {
                if (selectedOrganizationId) {
                  api.getCarePlansAndHealthSummaries(patientId!, selectedOrganizationId)
                    .then(data => setCarePlans(data.care_plans || []))
                }
              }}
            />
          </TabsContent>

          <TabsContent value="monthly-plans" className="mt-0">
            <PatientMonthlyPlansTab summaries={healthSummaries} loading={isLoading} />
          </TabsContent>

          <TabsContent value="action-items" className="mt-0">
            <PatientActionItemsTab
              patientId={patientId!}
              actionItems={actionItems}
              loading={isLoading}
              onCompleteItem={handleCompleteActionItem}
              onReopenItem={handleReopenActionItem}
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Modals */}
      <PatientConsentModal
        open={isConsentModalOpen}
        onOpenChange={setIsConsentModalOpen}
        patientId={patientId!}
        onSuccess={() => {
          if (selectedOrganizationId && patientId) {
            fetchPatientConsents(selectedOrganizationId, patientId)
              .then(consents => setPatientConsent(consents[0] || null))
              .catch(console.error)
          }
        }}
      />

      {selectedOrganizationId && patientId && (
        <CarePlanModal
          open={isCarePlanModalOpen}
          onOpenChange={setIsCarePlanModalOpen}
          patientId={patientId}
          organizationId={selectedOrganizationId}
          carePlan={selectedCarePlan}
          onSuccess={() => {
            api.getCarePlansAndHealthSummaries(patientId, selectedOrganizationId)
              .then(data => setCarePlans(data.care_plans || []))
              .catch(console.error)
          }}
        />
      )}
    </div>
  )
}
